import React, { useState, useEffect } from 'react';
import {
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    CircularProgress,
    Typography,
    Divider,
    useMediaQuery,
    useTheme as useMuiTheme,
    InputAdornment,
    IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createUser, updateUser, fetchBranches, fetchCities } from '../../store/slices/userSlice';
import { useTheme } from '../../contexts/ThemeContext';
import { User, CreateUserData, UpdateUserData } from '../../services/userApi';
import GenericModal from '../../components/GenericModal';

interface UserFormProps {
    open: boolean;
    user: User | null;
    onClose: () => void;
    onSuccess: (isEdit: boolean, userName: string) => void;
    onError?: (message: string) => void;
}

const UserForm: React.FC<UserFormProps> = ({ open, user, onClose, onSuccess, onError }) => {
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const dispatch = useAppDispatch();

    const { branches, cities } = useAppSelector((state) => state.users);
    const { user: currentUser } = useAppSelector((state) => state.auth);
    const isAdmin = currentUser?.type === 'ADMIN';

    // Form state
    const [formData, setFormData] = useState<CreateUserData>({
        name: '',
        email: '',
        type: 'MANAGER',
        password: '',
        phone: '',
        phone2: '',
        cnic: '',
        address: '',
        city_id: undefined,
        branch_id: undefined
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Load branches and cities on mount
    useEffect(() => {
        if (open) {
            dispatch(fetchBranches());
            dispatch(fetchCities());
        }
    }, [open, dispatch]);

    // Reset form when user changes
    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name,
                email: user.email,
                type: user.type,
                password: '',
                phone: user.profile?.phone || '',
                phone2: user.profile?.phone2 || '',
                cnic: user.profile?.cnic || '',
                address: user.profile?.address || '',
                city_id: user.profile?.city_id,
                branch_id: user.branch_id || undefined
            });
        } else {
            setFormData({
                name: '',
                email: '',
                type: 'MANAGER',
                password: '',
                phone: '',
                phone2: '',
                cnic: '',
                address: '',
                city_id: undefined,
                branch_id: undefined
            });
        }
        setErrors({});
    }, [user]);

    // Handle input changes
    const handleInputChange = (field: string, value: string | number | undefined) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    // Handle select changes
    const handleSelectChange = (field: string) => (event: SelectChangeEvent<string | number>) => {
        const value = event.target.value;
        handleInputChange(field, value === '' ? undefined : value);
    };

    // Validate form
    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!user && !formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password && formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        if (!formData.phone?.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^[0-9+\-\s()]+$/.test(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }

        if (formData.phone2 && !/^[0-9+\-\s()]+$/.test(formData.phone2)) {
            newErrors.phone2 = 'Please enter a valid phone number';
        }

        if (formData.cnic && !/^[0-9]{13}$/.test(formData.cnic.replace(/\D/g, ''))) {
            newErrors.cnic = 'Please enter a valid 13-digit CNIC';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            if (user) {
                // Update user
                const updateData: UpdateUserData = {
                    name: formData.name,
                    email: formData.email,
                    type: formData.type,
                    branch_id: formData.branch_id,
                    phone: formData.phone,
                    phone2: formData.phone2,
                    cnic: formData.cnic,
                    address: formData.address,
                    city_id: formData.city_id
                };

                // Include password only if admin is updating it
                if (isAdmin && formData.password) {
                    updateData.password = formData.password;
                }

                await dispatch(updateUser({ id: user.id, userData: updateData })).unwrap();
                onSuccess(true, formData.name);
            } else {
                // Create user
                await dispatch(createUser(formData)).unwrap();
                onSuccess(false, formData.name);
            }
        } catch (error: any) {
            console.error('Form submission error:', error);
            if (onError) {
                onError(error?.message || 'An error occurred while saving the user');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            onClose();
        }
    };

    return (
        <GenericModal
            open={open}
            onClose={handleClose}
            title={user ? 'Edit User' : 'Create New User'}
            maxWidth={isMobile ? "xs" : "md"}
            fullWidth
            actions={
                <>
                    <Button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        fullWidth={isMobile}
                        sx={{
                            color: theme.palette.text.secondary,
                            borderRadius: 3
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        fullWidth={isMobile}
                        onClick={handleSubmit}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            borderRadius: 3,
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                            }
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            user ? 'Update User' : 'Create User'
                        )}
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit}>
                <Grid container spacing={isMobile ? 2 : 3}>
                    {/* Basic Information */}
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                            Basic Information
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Full Name *"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                            disabled={isSubmitting}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Email Address *"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            error={!!errors.email}
                            helperText={errors.email}
                            disabled={isSubmitting}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth error={!!errors.type}>
                            <InputLabel>Role *</InputLabel>
                            <Select
                                value={formData.type}
                                onChange={handleSelectChange('type')}
                                label="Role *"
                                disabled={isSubmitting}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="ADMIN">Admin</MenuItem>
                                <MenuItem value="MANAGER">Manager</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel>Branch</InputLabel>
                            <Select
                                value={formData.branch_id || ''}
                                onChange={handleSelectChange('branch_id')}
                                label="Branch"
                                disabled={isSubmitting}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="">No Branch</MenuItem>
                                {branches.map((branch) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.name} ({branch.code})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {(!user || isAdmin) && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label={user ? "Password" : "Password *"}
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                error={!!errors.password}
                                helperText={errors.password || (user ? "Leave blank to keep current password" : "")}
                                disabled={isSubmitting}
                                placeholder={user ? "Leave blank to keep current password" : ""}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                disabled={isSubmitting}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                    )}

                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                            Contact Information
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Phone Number *"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            error={!!errors.phone}
                            helperText={errors.phone}
                            disabled={isSubmitting}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Secondary Phone"
                            value={formData.phone2}
                            onChange={(e) => handleInputChange('phone2', e.target.value)}
                            error={!!errors.phone2}
                            helperText={errors.phone2}
                            disabled={isSubmitting}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="CNIC"
                            value={formData.cnic}
                            onChange={(e) => handleInputChange('cnic', e.target.value)}
                            error={!!errors.cnic}
                            helperText={errors.cnic || '13 digits without dashes'}
                            disabled={isSubmitting}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel>City</InputLabel>
                            <Select
                                value={formData.city_id || ''}
                                onChange={handleSelectChange('city_id')}
                                label="City"
                                disabled={isSubmitting}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="">Select City</MenuItem>
                                {cities.map((city) => (
                                    <MenuItem key={city.id} value={city.id}>
                                        {city.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Address"
                            multiline
                            rows={3}
                            value={formData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            disabled={isSubmitting}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': {
                                        borderWidth: '1px',
                                    },
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </form>
        </GenericModal>
    );
};

export default UserForm; 